'use client'

import { CrossIcon } from '@repo/ui/CrossIcon'
import { LargeContactDetailsIcon } from '@repo/ui/LargeContactDetailsIcon'
import { PhoneNumberRow } from '@repo/ui/PhoneNumberRow'
import { phoneSchema } from '@repo/validation/schema_NewClient'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { useToastStore } from '@repo/store/useToastStore'
import { validate } from '@repo/validation/validation'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

export const NewClientPhoneNumbers = ({ updateStepState, wideMode, handlePrevNext }) => {
  const { clientPhones, updateState } = useNewClientStore()
  const theme = useTheme()
  const formRef = useRef()
  const [validationErrors, setValidationErrors] = useState({})
  const [stateToSave, setStateToSave] = useState({})
  const { showToast } = useToastStore()

  useEffect(() => {
    setStateToSave({
      clientPhones
    })
  }, [clientPhones])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setValidationErrors({})

    try {
      await validate(phoneSchema, stateToSave)
    } catch (err) {
      const { inner } = err
      const errors = {}
      let firstErrorKey
      inner.forEach(({ path, message }, index) => {
        errors[path] = message
        if (index === 0) firstErrorKey = CSS.escape(path)
      })

      console.log(errors)

      showToast({ type: 'error', text: 'Please check all fields for errors', title: 'Form errors' })

      // scroll to error if possible
      const container = document.querySelector('[data-testid="WizardStepContentsContainer"]')
      const errorElement = firstErrorKey
        ? document.querySelector(`[data-testid="WizardPanel"]:has([name=${firstErrorKey}])`)
        : null
      if (container && errorElement) container.scrollTop = errorElement.offsetParent?.offsetTop || 0

      setValidationErrors(errors)
      return
    }

    const count = clientPhones?.filter((item) => item.phoneNumber)?.length || 0
    const statusText = count === 1 ? '1 phone number added' : `${count} phone numbers added`
    updateStepState({ status: 'completed', statusText })
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  const addPhone = () => {
    updateState({
      key: 'clientPhones',
      value: [
        ...clientPhones,
        {
          id: new Date().getTime(),
          description: '',
          phoneType: null,
          phoneNumber: '',
          isMainNumber: clientPhones.length === 0
        }
      ]
    })
  }

  const removePhone = (removeIndex) => {
    updateState({
      key: 'clientPhones',
      value: clientPhones.filter((_, i) => i !== removeIndex)
    })
  }

  const updatePhone = ({ index, id, description, phoneType, phoneNumber, isMainNumber }) => {
    // build a new array
    const next = clientPhones.map((phone, i) => {
      if (i === index) {
        // update this one
        return { id, description, phoneType, phoneNumber, isMainNumber }
      }
      // if the newly-selected one is main, clear main flag on the others
      if (isMainNumber) {
        return { ...phone, isMainNumber: false }
      }
      return phone
    })

    updateState({
      key: 'clientPhones',
      value: next
    })
  }

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      <WizardPanel data-testid="NewClientPhoneNumbers">
        <WizardIconTitle
          title="Phone numbers"
          icon={LargeContactDetailsIcon}
          sx={{ mb: 1 }}
          isOptional
          text="Enter contact numbers for this client"
        />
      </WizardPanel>

      {clientPhones?.map((phoneDetails, index) => {
        return (
          <PhoneNumberRow
            key={`phoneDetails-${phoneDetails.id}`}
            defaultDescription={phoneDetails.description}
            defaultPhoneType={phoneDetails.phoneType}
            defaultPhoneNumber={phoneDetails.phoneNumber}
            defaultIsMainNumber={phoneDetails.isMainNumber}
            id={phoneDetails.id}
            index={index}
            onChange={updatePhone}
            onRemove={removePhone}
            validationErrors={validationErrors}
          />
        )
      })}

      <TextLinkButton
        onClick={addPhone}
        sx={{ mt: 5, ml: 5 }}
        title={
          <MUIStack direction="row" alignItems="center" gap={2}>
            <CrossIcon
              sx={{
                transform: 'rotate(45deg)',
                width: '24px',
                height: '24px',
                color: theme?.palette?.secondary?.dark || 'black'
              }}
            />
            Add new
          </MUIStack>
        }
      />

      <WizardFooter wideMode={wideMode} handleClickBack={handleClickBack} continueButtonIsFormSubmitButton />
    </form>
  )
}

NewClientPhoneNumbers.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func
}
